import { z } from 'zod'

/**
 * Images a template embeds — today, the masthead logo (CLI-695).
 *
 * An email cannot carry an image the way a web page does: the recipient's mail client fetches it
 * from a public URL at read time, so an uploaded logo has to end up at an address the whole
 * internet can reach. These are the two halves of that: the upload a signed-in editor makes, and
 * the open path the rendered email points at.
 *
 * Storage is Postgres for now — a handful of small logos, served straight from the row. That is
 * deliberate and temporary: it should move to a GCS bucket quickly (Kaushik, 2026-09-11), before
 * anyone uploads their tenth logo or a hero image. The path contract below is what makes the
 * move a redirect rather than a migration of every template document.
 */

export const TEMPLATE_ASSET_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/gif'] as const
export const templateAssetContentTypeSchema = z.enum(TEMPLATE_ASSET_CONTENT_TYPES)
export type TemplateAssetContentType = z.infer<typeof templateAssetContentTypeSchema>

/** One megabyte. A masthead logo is tens of kilobytes; anything near this is the wrong file. */
export const TEMPLATE_ASSET_MAX_BYTES = 1_000_000

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
/** The open path an uploaded image is served at, relative to the API's origin. */
export const TEMPLATE_ASSET_PATH_PATTERN = new RegExp(`^/api/public/assets/${UUID}$`, 'i')

export function templateAssetPath(id: string): string {
  return `/api/public/assets/${id}`
}

/**
 * The upload. Base64 in JSON rather than multipart, so it rides the same typed client as every
 * other call and the validator shapes its errors like every other 400. The cap is the byte cap
 * with base64's overhead, so an oversized file is refused before it is decoded.
 */
export const uploadTemplateAssetSchema = z.object({
  fileName: z.string().min(1).max(200),
  contentType: templateAssetContentTypeSchema,
  dataBase64: z
    .string()
    .min(1)
    .max(Math.ceil((TEMPLATE_ASSET_MAX_BYTES * 4) / 3) + 4),
  /** The account whose templates this belongs with; absent means the shared library. */
  accountId: z.uuid().optional(),
})
export type UploadTemplateAsset = z.infer<typeof uploadTemplateAssetSchema>

export const templateAssetSchema = z.object({
  id: z.uuid(),
  /** What to put in `logoUrl`: the open path, relative, resolved against the origin at render. */
  path: z.string(),
  fileName: z.string(),
  contentType: templateAssetContentTypeSchema,
  byteSize: z.number().int().nonnegative(),
  /** Pixel dimensions read from the file, when the header was readable. */
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  createdAt: z.iso.datetime(),
})
export type TemplateAsset = z.infer<typeof templateAssetSchema>
